import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
  { to: "/", label: "Home", testid: "nav-link-home" },
  { to: "/what-we-do", label: "What We Do", testid: "nav-link-what-we-do" },
  { to: "/pricing", label: "Packages & Pricing", testid: "nav-link-pricing" },
  { to: "/software", label: "Software", testid: "nav-link-software" },
  { to: "/faq", label: "FAQ", testid: "nav-link-faq" },
  { to: "/about", label: "About", testid: "nav-link-about" },
];

const useTorontoTime = () => {
  const [time, setTime] = useState("");
  useEffect(() => {
    const tick = () =>
      setTime(
        new Date().toLocaleTimeString("en-CA", {
          timeZone: "America/Toronto",
          hour: "numeric",
          minute: "2-digit",
        })
      );
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, []);
  return time;
};

export const SiteHeader = () => {
  const time = useTorontoTime();
  const [open, setOpen] = useState(false);

  return (
    <motion.header
      data-testid="nav-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className="fixed inset-x-0 top-0 z-50 border-b border-slate-200/80 bg-white/70 backdrop-blur-xl shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-6">
        <Link
          data-testid="nav-logo"
          to="/"
          aria-label="Covelli home"
          className="flex shrink-0 items-center"
        >
          <img src="/covelli-wordmark-ink.png" alt="Covelli logo" className="h-8 w-auto" />
        </Link>

        <nav className="hidden items-center gap-7 lg:flex">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={({ isActive }) =>
                `font-mono text-[11px] uppercase tracking-widest transition-colors duration-300 ${
                  isActive ? "text-kinetic" : "text-slate-500 hover:text-slate-900"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <div
            data-testid="nav-status-pill"
            className="hidden items-center gap-2 rounded-full border border-skybrand-border bg-skybrand-light px-4 py-1.5 xl:flex"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-skybrand animate-pulse-dot" />
            <span className="font-mono text-[11px] font-medium uppercase tracking-widest text-skybrand-hover">
              Accepting New Clients
            </span>
          </div>
          <span className="hidden font-mono text-[11px] uppercase tracking-widest text-slate-400 2xl:block">
            Toronto {time}
          </span>
          <Link
            to="/contact"
            data-testid="nav-book-call-button"
            className="rounded-full bg-kinetic px-5 py-2.5 font-display text-xs font-bold uppercase tracking-wide text-white transition-colors duration-300 hover:bg-kinetic-hover"
          >
            Book a Call
          </Link>
          <button
            data-testid="nav-mobile-menu-button"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-slate-700 lg:hidden"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-slate-200 bg-white lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {[...LINKS, { to: "/contact", label: "Contact / Book a Call", testid: "nav-link-contact" }].map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-${l.testid}`}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `rounded-lg px-3 py-3 font-mono text-xs uppercase tracking-widest transition-colors duration-300 ${
                      isActive ? "bg-kinetic-light text-kinetic" : "text-slate-600"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
